const MONTHS = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec"
];

const pad2 = (n) => String(n).padStart(2, "0");

// --- Parsing helpers (safe for date-only strings; no timezone shift) ---
function normalizeYMD(input) {
  if (!input && input !== 0) return null;

  if (input instanceof Date && !isNaN(input.getTime())) {
    return { y: input.getFullYear(), m: input.getMonth() + 1, d: input.getDate() };
  }

  const s = String(input).trim();

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, mm, dd] = s.split("-").map((x) => parseInt(x, 10));
    return { y, m: mm, d: dd };
  }

  // DD/MM/YYYY or DD-MM-YYYY
  let m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
  if (m) {
    let d = parseInt(m[1], 10);
    let mo = parseInt(m[2], 10);
    let y = parseInt(m[3], 10);
    if (y < 100) y += 2000;
    return { y, m: mo, d };
  }

  // Fallback to native Date
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) {
    return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
  }

  return null;
}

function minutesFromAnyTime(t) {
  if (!t && t !== 0) return null;
  const s = String(t).trim();

  // 24h "HH:mm"
  let m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    const hh = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) return hh * 60 + mm;
    return null;
  }

  // AM/PM "h:mm AM/PM" (allow '.' too)
  m = s.match(/^(\d{1,2})[:.](\d{2})\s*([AP]M)$/i);
  if (m) {
    let hh = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const ampm = m[3].toUpperCase();
    if (ampm === "PM" && hh < 12) hh += 12;
    if (ampm === "AM" && hh === 12) hh = 0;
    if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) return hh * 60 + mm;
  }
  return null;
}

function timeFromMinutes(mins) {
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h < 12 ? "am" : "pm";
  h = h % 12 || 12;
  return `${h}:${pad2(m)} ${ampm}`;
}

// --- Public API ---
export function formatDate(input, { monthCase = "lower" } = {}) {
  const ymd = normalizeYMD(input);
  if (!ymd) return String(input ?? "");
  const { y, m, d } = ymd;
  let monthName = MONTHS[m - 1] || "";
  if (monthCase === "capitalized" && monthName) {
    monthName = monthName[0].toUpperCase() + monthName.slice(1);
  }
  const yy = String(y).slice(-2);
  return `${d}-${monthName}-${yy}`; // e.g. 2-september-25
}

export function formatTime(input) {
  // Accept "HH:mm", "h:mm AM/PM", Date
  if (input instanceof Date && !isNaN(input.getTime())) {
    const mins = input.getHours() * 60 + input.getMinutes();
    return timeFromMinutes(mins);
  }
  const mins = minutesFromAnyTime(input);
  if (mins == null) return String(input ?? "");
  return timeFromMinutes(mins);
}

export function formatDateTime(input, opts) {
  // Accept Date, ISO string, timestamp
  let dt = null;
  if (input instanceof Date && !isNaN(input.getTime())) {
    dt = input;
  } else {
    const s = String(input ?? "").trim();
    const n = Number(s);
    if (!Number.isNaN(n) && s !== "") dt = new Date(n);
    else dt = new Date(s);
  }
  if (!dt || isNaN(dt.getTime())) return String(input ?? "");
  return `${formatDate(dt, opts)}, ${formatTime(dt)}`; // e.g. 2-september-25, 12:30 pm
}

// Combine a date string (YYYY-MM-DD / etc) with a time string and format
export function formatDateTimeFromParts(dateStr, timeStr, opts) {
  const ymd = normalizeYMD(dateStr);
  if (!ymd) return String(`${dateStr} ${timeStr}`.trim());
  const mins = minutesFromAnyTime(timeStr) ?? 0;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const dt = new Date(ymd.y, ymd.m - 1, ymd.d, h, m, 0, 0); // local time
  return formatDateTime(dt, opts);
}







export function formatYearMonth(input /* "YYYY-MM" or Date */) {
  let y, m;
  if (input instanceof Date && !isNaN(input.getTime())) {
    y = input.getFullYear();
    m = input.getMonth() + 1;
  } else if (typeof input === "string") {
    const m2 = input.trim().match(/^(\d{4})-(\d{1,2})$/);
    if (m2) {
      y = parseInt(m2[1], 10);
      m = parseInt(m2[2], 10);
    }
  }
  if (!y || !m) return String(input ?? "");
  const monthName = ["January","February","March","April","May","June","July","August","September","October","November","December"][m - 1] || "";
  return `${monthName}, ${y}`;
}



export function formatDateLong(input) {
  const ymd = normalizeYMD(input);
  if (!ymd) return String(input ?? "");
  const { y, m, d } = ymd;
  const monthName =
    ["January","February","March","April","May","June","July","August","September","October","November","December"][m - 1] || "";
  return `${pad2(d)}-${monthName}-${y}`;
}
